import React from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';

import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import Typography from '@material-ui/core/Typography';
import SnackBar from '@material-ui/core/Snackbar';
import SplitPageContainer from 'components/SplitPageContainer';
import SplitPageRight from 'components/SplitPageRight';

import withStyles from '@material-ui/core/styles/withStyles';

const styles = theme => {
  return {
    header: {
      fontSize: 50.9,
      color: theme.palette.primary.main,
      marginTop: theme.spacing(5),
    },
    subheader: {
      fontSize: 17,
      lineHeight: 1.41,
      letterSpacing: 0.2,
    },
    textfield: {
      maxWidth: 400,
      width: '100%',
      marginTop: theme.spacing(4),
    },
    button: {
      fontWeight: 'bold',
      fontSize: 14.6,
      [theme.breakpoints.down('xs')]: {
        alignItems: 'center',
      },
    },
    forgotButton: {
      marginLeft: theme.spacing(),
      textTransform: 'none',
      [theme.breakpoints.down('sm')]: {
        alignItems: 'center',
        marginLeft: 0,
        marginTop: theme.spacing(3),
      },
    },
    domainButton: {
      marginTop: theme.spacing(6),
      color: theme.palette.secondary.main,
    },
    buttonLayout: {
      display: 'flex',
      flexDirection: 'row',
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'center',
        alignContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
      },
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignContent: 'flex-start',
      alignItems: 'flex-start',
      flexDirection: 'column',
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'center',
        alignContent: 'center',
        alignItems: 'center',
      },
    },
    input: {
      minWidth: 340,
      minHeight: 70,
      marginTop: theme.spacing(4),
      [theme.breakpoints.down('xs')]: {
        minWidth: '100%',
      },
    },
    labelSize: {
      fontSize: 24,
      paddingBottom: theme.spacing(1),
    },
    inputSize: {
      fontSize: 28,
      marginTop: theme.spacing(2),
    },
    disabled: {
      borderColor: theme.palette.grey[300],
    },
    error: {
      marginTop: theme.spacing(2),
      color: theme.palette.error.main,
      transition: 'opacity .3s',
      fontSize: 15,
    },
    placeholder: {
      marginBottom: theme.spacing(4),
    },
  };
};

class StandardLogin extends React.PureComponent {
  static propTypes = {
    forgotPassword: PropTypes.func.isRequired,
    loginWithPassword: PropTypes.func.isRequired,
    email: PropTypes.string,
    status: PropTypes.object.isRequired,
    classes: PropTypes.object.isRequired,
    passwordLoginResponse: PropTypes.object.isRequired,
    loginDomains: PropTypes.array.isRequired,
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    additionalParams: PropTypes.object.isRequired,
  };

  state = {
    password: '',
    open: false,
    errorState: false,
    error: '',
  };

  componentDidUpdate(prevProps, prevState) {
    if (!prevProps.status.hasError() && this.props.status.hasError()) {
      this.updateError();
    }
    if (prevProps.status.isLoading() && this.props.status.isNormal()) {
      this.standardSubmit();
    }
    this.setErrorState(prevProps, prevState);
  }

  componentDidMount() {
    if (!this.props.email) {
      this.props.history.push(`/`);
    }
  }

  standardSubmit = () => {
    const { loginDomains, match, passwordLoginResponse } = this.props;
    const { redirectTo } = passwordLoginResponse;

    if (redirectTo) {
      window.location.href = redirectTo;
      return;
    }

    const hostname = window.location.hostname;

    // Remove the subdomain, ex: login.justsift.com -> justsift.com
    const domainName = hostname.slice(hostname.indexOf('.') + 1);

    const baseUrl = `https://${
      loginDomains[_get(match, 'params.id', null)].subdomain
    }.${domainName}`;

    const redirectPath = this.props.additionalParams.redirectTo || '';
    window.location.href = `${baseUrl}${redirectPath}`;
  };

  handleChange = event => {
    this.setState({ password: event.target.value });
  };

  handleSubmit = event => {
    event.preventDefault();

    if (this.isValidPassword(this.state.password)) {
      this.signInSubmit();
    }
  };

  setErrorState = (prevProps, prevState) => {
    if (prevProps !== undefined && prevProps.status.isLoading() && this.props.status.hasError()) {
      this.setState({ errorState: true });
    } else if (prevState !== undefined && prevState.password !== this.state.password) {
      this.setState({ errorState: false });
    }
  };

  signInSubmit = () => {
    const { email, loginDomains, match, loginWithPassword, additionalParams } = this.props;
    const loginOptionResponse = loginDomains[_get(match, 'params.id', null)];
    const { clientId, loginOptions } = loginOptionResponse;

    const setCookies = true;
    const directoryId =
      loginOptions.directoryId && !loginOptions.useClientPasswordMethod
        ? loginOptions.directoryId
        : null;

    loginWithPassword(
      email,
      this.state.password,
      setCookies,
      clientId,
      directoryId,
      additionalParams,
    );
  };

  forgotPassword = () => {
    const { email, loginDomains, match, forgotPassword } = this.props;

    const loginOptionResponse = loginDomains[_get(match, 'params.id', null)];
    const { clientId, loginOptions } = loginOptionResponse;

    const directoryId = loginOptions.directoryId || null;
    forgotPassword(email, clientId, directoryId);

    this.setState({ open: !this.state.open });
  };

  updateError = () => {
    this.setState({ error: "Whoops! That isn't the correct password for " + this.props.email });
    if (this.passwordInput) {
      this.passwordInput.select();
    }
  };

  isValidPassword = password => {
    if (password.length > 0) {
      return true;
    } else {
      return false;
    }
  };

  displayError = () => {
    const { classes } = this.props;
    return (
      <Typography
        className={classes.error}
        style={{
          opacity: this.state.errorState ? 1 : 0,
          visibility: this.state.errorState ? 'visible' : 'hidden',
        }}
      >
        {this.state.error ? this.state.error : 'error'}
      </Typography>
    );
  };

  goBack = () => {
    const { loginDomains, match, history } = this.props;
    const loginOptionsArray = loginDomains[match.params.id].loginOptions.preferredMethods.concat(
      loginDomains[match.params.id].loginOptions.otherMethods,
    );
    if (loginOptionsArray.length === 1 && loginDomains.length === 1) {
      history.push('/');
    } else {
      history.goBack();
    }
  };

  handleKeyPress = event => {
    if (event.key === 'Enter') {
      this.handleSubmit(event);
    }
  };

  render() {
    const { classes, email, status } = this.props;
    const password = this.state.password;

    if (email.length === 0) {
      return null;
    }

    return (
      <SplitPageContainer>
        <SplitPageRight>
          <SnackBar
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            open={this.state.open}
            ContentProps={{ 'aria-describedby': 'message-id' }}
            message={
              <span id="message-id">
                A password reset email has been sent to {this.props.email}
              </span>
            }
          />
          <Typography className={classes.header} variant="h2">
            Sign In
          </Typography>
          <Typography className={classes.subheader} variant="body1">
            Enter your password
          </Typography>
          <FormControl className={classes.input}>
            <InputLabel className={classes.labelSize}>Your Password</InputLabel>
            <Input
              autoFocus
              error={this.state.errorState}
              className={classes.inputSize}
              type={'password'}
              inputRef={input => {
                this.passwordInput = input;
              }}
              onChange={this.handleChange}
              value={password}
              onKeyPress={this.handleKeyPress}
            />
          </FormControl>
          {this.displayError()}
          <div className={classes.buttonLayout}>
            <div>
              <Button
                variant="contained"
                color="secondary"
                disabled={!this.isValidPassword(password) || status.isLoading()}
                itemType="submit"
                onClick={this.signInSubmit}
                className={classes.button}
                classes={{ disabled: classes.disabled }}
              >
                {status.isLoading() ? 'Loading' : 'Sign In'}
              </Button>
            </div>
            <div>
              <Button onClick={this.forgotPassword} className={classes.forgotButton}>
                Forgot your password?
              </Button>
            </div>
          </div>
          <div className={classes.buttonContainer}>
            <Button className={classes.domainButton} onClick={this.goBack}>
              Back
            </Button>
          </div>
        </SplitPageRight>
      </SplitPageContainer>
    );
  }
}

export default withStyles(styles)(StandardLogin);
